import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

export interface PublishedPolicy {
  version: string;
  effectiveAt: string;
  body: string;
  source: "db" | "fallback";
}

/**
 * Fetches the most recent privacy_policy_versions row for a given type
 * and returns the localized body. Falls back to undefined if nothing
 * has been published yet — callers should render the legacy translated
 * sections in that case.
 */
export function usePublishedPolicy(policyType: "privacy" | "cookies" | "terms") {
  const { i18n } = useTranslation();
  const isPt = (i18n.language ?? "en").startsWith("pt");
  const [policy, setPolicy] = useState<PublishedPolicy | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("privacy_policy_versions")
        .select("version, effective_at, content_pt, content_en")
        .eq("policy_type", policyType)
        .lte("effective_at", new Date().toISOString())
        .order("effective_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        const body =
          (isPt ? (data.content_pt as any)?.body : (data.content_en as any)?.body) ?? "";
        if (body && body.trim().length > 0) {
          setPolicy({
            version: data.version,
            effectiveAt: data.effective_at,
            body,
            source: "db",
          });
        } else {
          setPolicy(undefined);
        }
      } else {
        setPolicy(undefined);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [policyType, isPt]);

  return { policy, loading };
}
